/**
 * Bounded render queue for the document viewer.
 *
 * pdf.js renders are CPU-heavy: each call decodes, lays out, and rasterises
 * a full page into a canvas. Kicking off N concurrent renders when the user
 * fast-scrolls through a 200-page document hammers the main thread (or the
 * worker pool) and tanks scroll responsiveness. This queue serialises render
 * requests up to `concurrency` at once, with the rest waiting their turn.
 *
 * Key behaviours:
 *   - `concurrency` slots run in parallel; everything else queues FIFO.
 *   - A submitted job carries an opaque "key" so callers can detect that the
 *     work they queued is now stale (different scale, rotation, dpr, …) and
 *     cancel it before pdf.js does the expensive part.
 *   - `cancel(key)` drops queued jobs with that key. In-flight work is the
 *     caller's responsibility (PageCanvas tracks `cancelled` and ignores the
 *     resolve/reject).
 *
 * Intentionally framework-agnostic so we can unit-test the policy without
 * spinning up React. The viewer plumbs a single shared queue through props.
 */

export type RenderTask = () => Promise<void>

export interface RenderJob {
  /** Opaque caller-defined identity used by `cancel`. */
  key: string
  task: RenderTask
}

interface PendingJob extends RenderJob {
  resolve: () => void
  reject: (err: unknown) => void
}

export class RenderQueue {
  private readonly concurrency: number
  private active = 0
  private queue: PendingJob[] = []

  constructor(concurrency = 2) {
    if (!Number.isFinite(concurrency) || concurrency < 1) {
      throw new Error(`RenderQueue concurrency must be >= 1, got ${concurrency}`)
    }
    this.concurrency = Math.floor(concurrency)
  }

  /**
   * Enqueue a render. Returns a promise that resolves when the task
   * completes, or rejects if the task throws (or the queued job is
   * dropped before it starts via `cancel`).
   */
  submit(job: RenderJob): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.queue.push({ ...job, resolve, reject })
      this.pump()
    })
  }

  /**
   * Drop any queued (not-yet-started) job whose key matches. Returns the
   * number of queued jobs dropped — useful for tests that want to verify
   * cancellation actually fired.
   *
   * In-flight jobs are NOT cancelled here: pdf.js exposes its own
   * RenderTask.cancel() on the task it returns, and PageCanvas owns that
   * lifetime. The queue's job is purely admission control.
   */
  cancel(key: string): number {
    const next: PendingJob[] = []
    let dropped = 0
    for (const job of this.queue) {
      if (job.key === key) {
        job.reject(new RenderQueueCancelled(key))
        dropped++
      } else {
        next.push(job)
      }
    }
    this.queue = next
    return dropped
  }

  /** Active + queued depth. Exposed for tests + dev observability. */
  get pendingCount(): number {
    return this.active + this.queue.length
  }

  /** Slots currently executing. */
  get activeCount(): number {
    return this.active
  }

  /** Concurrency cap configured at construction time. */
  get maxConcurrent(): number {
    return this.concurrency
  }

  private pump(): void {
    while (this.active < this.concurrency && this.queue.length > 0) {
      const job = this.queue.shift()!
      this.active += 1
      // Catch synchronous throws so an exploding task() doesn't bypass the
      // queue's `active` accounting and stall future jobs forever.
      Promise.resolve()
        .then(job.task)
        .then(
          () => {
            this.active -= 1
            job.resolve()
            this.pump()
          },
          (err: unknown) => {
            this.active -= 1
            job.reject(err)
            this.pump()
          },
        )
    }
  }
}

/** Thrown when a queued job is dropped via `cancel(key)` before it starts. */
export class RenderQueueCancelled extends Error {
  readonly key: string
  constructor(key: string) {
    super(`Render queue cancelled job ${key}`)
    this.name = 'RenderQueueCancelled'
    this.key = key
  }
}

/**
 * Module-scoped default queue used by the viewer when consumers don't pass
 * one in. Concurrency=2 matches typical desktop cores devoted to canvas +
 * pdf.js worker without starving the UI thread.
 */
export const defaultRenderQueue = new RenderQueue(2)
